import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, ImageOff } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { thumbnailUrl } from '../../lib/treatmentReferences';
import ShotNumber from './ShotNumber';

/**
 * One shot's frames, large. Shared by the editor and the shared page.
 *
 * Shows the address as linked - for a Frameset frame that's the full-size
 * image, not the editor's thumbnail - falling back to the thumbnail, then to
 * a message with the link. Arrow keys step through the frames.
 *
 * `frames` is [{ id, image_url, source_url, note }]; `index` is the open one,
 * or null when closed. `number` is the shot's number, shown before its title.
 */
export default function FrameLightbox({ frames, index, onIndexChange, title, number }) {
  const open = index !== null && index !== undefined && frames[index];
  const frame = open ? frames[index] : null;
  const [attempt, setAttempt] = useState(0);

  useEffect(() => setAttempt(0), [index]);

  const count = frames.length;
  const step = (delta) => onIndexChange((index + delta + count) % count);
  const sources = frame ? [...new Set([frame.image_url, thumbnailUrl(frame.image_url)])] : [];

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onIndexChange(null)}>
      <DialogContent
        className="max-w-6xl w-[calc(100vw-2rem)] p-4 sm:p-6 gap-4"
        onKeyDown={(e) => {
          if (count < 2) return;
          if (e.key === 'ArrowRight') step(1);
          if (e.key === 'ArrowLeft') step(-1);
        }}
      >
        <DialogHeader className="pr-8">
          <DialogTitle className="flex items-center gap-2">
            {number && <ShotNumber number={number} />}
            {title || 'Reference frame'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Frame {index + 1} of {count}{frame?.note ? `: ${frame.note}` : ''}
          </DialogDescription>
        </DialogHeader>
        {frame && (
          <>
            <div className="flex items-center justify-center rounded-cards overflow-hidden bg-mist min-h-[200px]">
              {attempt < sources.length ? (
                <img
                  key={sources[attempt]}
                  src={sources[attempt]}
                  alt={frame.note || `Frame ${index + 1}`}
                  referrerPolicy="no-referrer"
                  className="max-h-[70vh] w-auto max-w-full object-contain"
                  onError={() => setAttempt((a) => a + 1)}
                />
              ) : (
                <div className="py-24 flex flex-col items-center gap-2 text-sm text-muted-foreground">
                  <ImageOff className="w-6 h-6" aria-hidden="true" />
                  Can't load this frame
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <span className="text-muted-foreground italic min-w-0 truncate">{frame.note}</span>
              <div className="flex items-center gap-2 shrink-0 ml-auto">
                {count > 1 && (
                  <>
                    <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Previous frame" onClick={() => step(-1)}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-muted-foreground tabular-nums px-1">{index + 1} / {count}</span>
                    <Button variant="outline" size="icon" className="h-8 w-8" aria-label="Next frame" onClick={() => step(1)}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </>
                )}
                <Button variant="outline" size="sm" asChild>
                  <a href={frame.source_url || frame.image_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4" /> {frame.source_url ? 'Source' : 'Full size'}
                  </a>
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
